/**
 * Message tree for the companion surface. Every namespace now comes from the
 * split `@4f/i18n` locale files — `common`, `chat`, `builder`, `gallery`,
 * `billing`, `referral`, `errors`, plus `surface`, `settings` and `auth`, which
 * used to be re-typed here as app-local English literals (untranslatable, and
 * invisible to the split-bundle loader). Nothing is re-typed.
 *
 * The ONLY thing layered on top is {@link surfaceExtras}: keys this app added
 * during the v1 release sweep that do not have a shared home YET. They are
 * candidates for promotion into `@4f/i18n`'s `surface` namespace — keeping them
 * in one clearly-named object is what makes that promotion a move, not a hunt.
 *
 * JSX-free so the tree stays trivially portable + testable.
 */
import {
  auth,
  billing,
  builder,
  chat,
  common,
  errors,
  gallery,
  type MessageTree,
  referral,
  settings,
  surface,
} from '@4f/i18n';

/**
 * App-local additions to the shared `surface` namespace, pending promotion.
 *
 * Duplicates are deliberately NOT declared here — `common.actions.retry`,
 * `gallery.actions.download` and `gallery.empty` are the shared homes for the
 * three keys this tree used to shadow. NOTE the `common` namespace is SPREAD at
 * the root of this tree (the pre-existing convention), so its call-site key is
 * `actions.retry`, not `common.actions.retry`.
 */
const surfaceExtras = {
  // Overrides the shared copy: the stream error now renders a REAL retry button
  // beside it, so the line no longer tells the user to tap something inert.
  streamError: 'Something went wrong.',
  // In-character tool activity — the run channel carries RAW tool names
  // (`generate_image`, `web_search`); showing those breaks the illusion.
  tools: {
    generic: 'One moment…',
    generate_image: 'Taking a photo for you…',
    generate_video: 'Recording a clip for you…',
    web_search: 'Looking something up…',
  },
  // Accessible name for an inline gallery tile — the directive carries only an
  // opaque asset id, and a raw UUID is what a screen reader would otherwise read.
  galleryItem: 'Generated media {index}',
  // The account nav holds Settings + Billing + Referrals + Sign-out.
  accountNav: 'Account',
  // Plan CTA — the price comes from the shared public pricing catalog.
  subscribeFor: 'Subscribe · {price}/mo',
  catalogEmpty: 'No ready-made companions are available right now.',
  // A turn typed with no connection, durably queued for replay on reconnect.
  queuedMessage: 'Waiting to send',
} as const;

/** The shared `surface` namespace plus this app's pending-promotion additions. */
const appSurface = { ...surface, ...surfaceExtras } as const;

/** Every shared namespace, with the app's `surface` extras layered on. */
export const surfaceMessages: MessageTree = {
  ...common,
  chat,
  builder,
  gallery,
  billing,
  referral,
  errors,
  surface: appSurface,
  settings,
  auth,
};

/** Default-locale backfill for any key missing from the active locale. */
export const fallbackMessages: MessageTree = surfaceMessages;
